import {
  INVISIBLE_DURATION,
  INVISIBLE_DURATION_EVENT,
  INVISIBLE_START_EVENT
} from "../support/tokens";

import {
  noop,
  isFunction,
  isPlainObject,
  objectWithAccessors,
  withProperties
} from "../../utils";

import { elapsedSeconds } from "../support/helpers";
import Supports from "../../utils/supports";

function __createFallbackWatcher__() {
  let TIMESTAMP;

  return __createWatcher__({
    create(_, timestamp) { TIMESTAMP = timestamp },
    release() { TIMESTAMP = undefined },
    elapsed(_, timestamp) { if (TIMESTAMP) return timestamp - TIMESTAMP }
  });
}

function __createPerformanceWatcher__() {
  return __createWatcher__({
    create(label) { return performance.mark(label) },
    release(label) { return performance.clearMarks(label) },
    elapsed(label) {
      const mark = performance.getEntriesByType("mark")
        .filter(({ name }) => name === label)[0];
      if (mark) return performance.now() - mark.startTime;
    }
  });
}

function __createStorageWatcher__() {
  return __createWatcher__({
    create(label, timestamp) { return localStorage.setItem(label, timestamp) },
    release(label) { return localStorage.removeItem(label) },
    elapsed(label, timestamp) {
      const mark = localStorage.getItem(label);
      if (mark) return timestamp - mark;
    }
  });
}

function __createWatcher__(init) {
  let MARKER, TIMESTAMP;

  const {
    create: __create__,
    release: __release__,
    elapsed: __getElapsedTime__
  } = isPlainObject(init) ? init : {};

  if ([__create__, __release__, __getElapsedTime__].every(isFunction)) {
    return {
      invisible() {
        createMarker();
        dispatchEvent(new CustomEvent(INVISIBLE_START_EVENT, {
          bubbles: false, cancelable: false
        }));
      },

      visible() {
        const invisibleDuration = +getMarkerElapsedTime();

        if (invisibleDuration) {
          const detail = Object.create(objectWithAccessors({
            [INVISIBLE_DURATION]: () => invisibleDuration
          }, true));

          Object.freeze(withProperties(detail, {
            exact: invisibleDuration,
            seconds: elapsedSeconds(invisibleDuration),
            timestamp: TIMESTAMP
          }));

          dispatchEvent(new CustomEvent(INVISIBLE_DURATION_EVENT, {
            detail, bubbles: false, cancelable: false
          }));
        }

        releaseMarker();
      }
    };
  }

  return { invisible: noop, visible: noop };

  function createMarker() {
    if (!MARKER) {
      MARKER = `__${
        btoa(String(Math.random()))
          .replace(/[+=\/]/g, "")
          .slice(-3)
      }__`;
    }

    __create__(MARKER, TIMESTAMP = Date.now());
  }

  function releaseMarker() {
    if (MARKER) __release__(MARKER, Date.now());
    MARKER = TIMESTAMP = undefined;
  }

  function getMarkerElapsedTime() {
    return __getElapsedTime__(MARKER, Date.now());
  }
}

export default function createWatcher() {
  return (
    Supports.performance && __createPerformanceWatcher__() ||
    Supports.localStorage && __createStorageWatcher__() ||
    __createFallbackWatcher__()
  );
}
